<template>
  <div class="add-ons" v-if="certificationsList.length">
    <div class="add-ons__content">
      <div class="add-ons__title">Add-ons</div>
      <div class="add-ons__items">
        <div
          class="add-ons__item"
          v-for="certification in certificationsList"
          :key="certification.merchant_id"
        >
          <div class="add-ons__item-check">
            <CheckGreen class="add-ons__item-check-included" v-if="certification.included" />
            <CheckBox
              v-else
              :cartTheme="true"
              :isSelected="certification.addOnChecked"
              @click-handler="updateSelectedCertification(certification)"
            />
          </div>
          <div class="add-ons__item-name">
            <div class="add-ons__item-name-text">{{ certification.name }}</div>
            <div class="add-ons__item-name-included" v-if="certification.included">
              {{
                isFreeTrial
                  ? 'Free with this subscription after trial period ends'
                  : 'Free with this subscription'
              }}
            </div>
          </div>
          <div class="add-ons__item-price">
            {{ certification.included ? 'Free' : certification.price }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import CheckGreenImg from '@/assets/icons/check_green.svg'
import CheckBox from '@/components/shared/CheckBox.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'AddOns',
  components: {
    CheckBox,
    CheckGreen: { ...CheckGreenImg, name: 'CheckGreenImg' },
  },
  data() {
    return {
      certificationsList: [],
    }
  },
  props: {
    certifications: {
      type: Array,
      required: true,
    },
    selectedPlanCode: {
      type: String,
      required: false,
    },
    isFreeTrial: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...mapState({
      shoppingCart: state => state.shoppingCart.shoppingCart,
    }),
    ...mapGetters({
      isOnTrialSubscription: 'subscriptions/isOnTrialSubscription',
    }),
    isCartFlowPage() {
      return ['payment', 'confirmation'].includes(this.$route.name)
    },
    cartHasSubscription() {
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      return itemsId.some(code => Object.values(CONSTANTS.PLANS).includes(code))
    },
  },
  methods: {
    ...mapActions({
      addToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
    }),
    updateSelectedCertification(certification) {
      const exists = this.shoppingCart.find(item => item.id === certification.id)
      if (exists) {
        certification.addOn = false
        certification.addOnChecked = false
        this.removeFromShoppingCart(certification)
      } else {
        certification.addOn = true
        certification.addOnChecked = true
        this.addToShoppingCart(certification)
      }
    },
    setCertificationsList() {
      if (this.isCartFlowPage) {
        // if user isOnTrialSubscription period, user should not be able to buy VOUCHERS.EJPT_V2
        if (this.isOnTrialSubscription) {
          this.certificationsList = this.certifications.filter(
            item => item.merchant_id !== CONSTANTS.VOUCHERS.EJPT_V2
          )
        } else {
          this.certificationsList = this.certifications
        }
      } else {
        const itemsId = this.shoppingCart.map(item => item.merchant_id)
        // remove VOUCHERS.EJPT_V2 if cart Has NO Subscription
        if (!this.cartHasSubscription) {
          const vouchersEjptV2 = itemsId.includes(CONSTANTS.VOUCHERS.EJPT_V2)
          if (vouchersEjptV2) {
            const vouchersEjptV2Obj = this.shoppingCart.find(item => {
              return item.merchant_id === CONSTANTS.VOUCHERS.EJPT_V2
            })
            this.removeFromShoppingCart(vouchersEjptV2Obj)
            vouchersEjptV2Obj.addOn = false
            vouchersEjptV2Obj.addOnChecked = false
          }
          // adding voucher merchant_id so is not added on the list of addOns by the filter
          itemsId.push(CONSTANTS.VOUCHERS.EJPT_V2)
          const cartHasMonthlyEjptV2 = itemsId.includes([
            CONSTANTS.PLANS.MONTHLY_EJPTV2,
            CONSTANTS.PLANS.QUARTERLY_EJPTV2,
          ])
          if (cartHasMonthlyEjptV2) ItemsId.splice(itemsId.indexOf(CONSTANTS.VOUCHERS.EJPT_V2), 1)
        }
        // if voucher was added to cart as AddOn, it should remain as an AddOn
        const vouchersOnCart = itemsId.some(code =>
          [CONSTANTS.VOUCHERS.ICCA, CONSTANTS.VOUCHERS.EJPT_V2].includes(code)
        )
        if (vouchersOnCart) {
          this.certifications.forEach(item => {
            if (item.addOn) {
              itemsId.splice(itemsId.indexOf(item.merchant_id), 1)
            }
          })
        }
        this.certificationsList = this.certifications.filter(item => {
          return !itemsId.includes(item.merchant_id)
        })
      }
    },
  },
  mounted() {
    this.setCertificationsList()
  },
  watch: {
    shoppingCart() {
      this.certificationsList.forEach(item => {
        if (this.shoppingCart.length === 0) item.addOnChecked = false
        if (this.selectedPlanCode === '') {
          item.included = false
        }
      })
      if (!this.cartHasSubscription) this.setCertificationsList()
      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss">
.add-ons {
  &__content {
    margin: 20px 0;
    text-align: left;
  }
  &__title {
    font-weight: 700;
    font-size: rem(20px);
    line-height: 26px;
    margin-bottom: 10px;
  }
  &__items {
    display: flex;
    flex-flow: column;
    row-gap: 10px;
  }
  &__item {
    display: grid;
    grid-template-columns: 35px auto 70px;
    padding: 15px;
    border: 1px solid var(--custom-list-bottom-border);
    border-radius: 10px;
    &-check {
      margin-right: 10px;
      display: flex;
      &-included {
        margin: 0 auto;
        position: relative;
        top: 10px;
      }
    }
    &-name {
      font-weight: 500;
      font-size: rem(16px);
      line-height: 23px;
      &-included {
        font-weight: 450;
        font-size: rem(12px);
        line-height: 14px;
        color: var(--strong-light-green-b5);
        margin-top: 5px;
      }
    }
    &-price {
      text-align: right;
      font-weight: 500;
      font-size: rem(18px);
      line-height: 24px;
    }
  }
}
</style>
